import { useState } from 'react';
import { Printer, ChevronDown, ChevronRight, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const steps = [
  {
    id: 1,
    title: 'Crear Lote de Facturas',
    location: 'Compras > Firma VB por Lote',
    desc: 'Compras agrupa las facturas que necesitan aprobación de Gerencia en un lote.',
    substeps: [
      'Ir a **Compras > Firma VB por Lote** en el menú lateral.',
      'Hacer clic en el botón **"+ Crear Lote"** (esquina superior derecha).',
      'Ingresar un **nombre descriptivo** para el lote (ej: "Lote Marzo Semana 4").',
      'Buscar facturas usando el campo **"Buscar por N° OC, Factura o Proveedor"**. Se pueden buscar por número de OC, número de factura o nombre del proveedor.',
      'También se puede buscar por **N° de Recepción** en el campo secundario. Al encontrarla, se muestra el detalle con proveedor, OC, monto neto e IVA.',
      'Verificar que el **Monto Total** sea correcto. Si no lo es, se puede editar manualmente antes de agregar.',
      'Hacer clic en **"Agregar"** para incluir cada factura al lote.',
      'Repetir para todas las facturas que se deseen incluir en el lote.',
      'Una vez agregadas todas las facturas, hacer clic en **"Crear Lote"**.',
    ],
    result: 'El lote se crea con estado "Pendiente VB" y aparece en la lista principal.',
  },
  {
    id: 2,
    title: 'Revisar Documentos del Lote',
    location: 'Compras > Firma VB por Lote',
    desc: 'Antes de dar el VB, Gerencia puede revisar los documentos adjuntos de cada factura.',
    substeps: [
      'En la lista de lotes, hacer clic en el ícono de **ojo (Ver detalle)** del lote a revisar.',
      'Se abre un panel con el resumen del lote: nombre, estado, cantidad de facturas, monto total y creador.',
      'Para cada factura se muestra: N° OC, N° Recepción, Proveedor, N° Factura y Monto Total.',
      'Hacer clic en el **número de OC** (enlace azul) para previsualizar el PDF de la Orden de Compra.',
      'Hacer clic en el **número de Recepción** (enlace azul) para previsualizar el PDF de la Recepción.',
      'También se pueden usar los íconos de **documento (OC)** y **escáner (Recepción)** en la columna de acciones.',
      'El visor de PDF se abre dentro de la app. Cerrar con el botón **"✕"** o haciendo clic fuera del visor.',
    ],
    result: 'Gerencia tiene visibilidad completa de los documentos antes de aprobar.',
  },
  {
    id: 3,
    title: 'Dar Visto Bueno (VB) al Lote',
    location: 'Compras > Firma VB por Lote',
    desc: 'Gerencia aprueba o rechaza cada factura del lote, con comentarios opcionales.',
    substeps: [
      'Hacer clic en el ícono de **firma/VB** del lote (segundo botón de acciones).',
      'Se abre el diálogo de Visto Bueno con todas las facturas del lote.',
      'Para cada factura, seleccionar **"Aprobar"** o **"Rechazar"**.',
      'Opcionalmente, agregar un **comentario** para cada factura (útil en rechazos para indicar el motivo).',
      'Una vez definidas todas las acciones, hacer clic en **"Confirmar VB"**.',
      'El sistema actualiza el estado de cada factura según la acción seleccionada.',
    ],
    result: 'Las facturas aprobadas quedan listas para enviar a contabilizar. Las rechazadas se marcan con el motivo.',
  },
  {
    id: 4,
    title: 'Enviar Lote a Contabilizar',
    location: 'Compras > Firma VB por Lote',
    desc: 'Una vez aprobado, el lote se envía a la pantalla de "Facturas por Contabilizar".',
    substeps: [
      'El lote aprobado muestra el botón **"Enviar a Contabilizar"** (ícono de envío, color índigo).',
      'Hacer clic en el botón de envío.',
      'Confirmar la acción en el diálogo que aparece.',
      'El sistema traslada las facturas aprobadas a la sección de **"Facturas por Contabilizar"**.',
      'El estado del lote cambia a **"Enviado a Contabilizar"**.',
    ],
    result: 'Las facturas aparecen en Contabilidad > Facturas por Contabilizar, agrupadas bajo el nombre del lote.',
  },
  {
    id: 5,
    title: 'Contabilizar en Alegra',
    location: 'Contabilidad > Facturas por Contabilizar',
    desc: 'Contabilidad revisa las facturas aprobadas y las envía al sistema contable Alegra.',
    substeps: [
      'Ir a **Contabilidad > Facturas por Contabilizar** en el menú lateral.',
      'En la parte superior aparecen los **Lotes VB** enviados por Gerencia, cada uno con su nombre y cantidad de facturas.',
      'También aparecen facturas individuales que fueron agregadas manualmente o por sugerencia automática.',
      'Revisar que las facturas sean correctas (proveedor, monto, OC).',
      'Seleccionar las facturas a contabilizar usando las **casillas de verificación**.',
      'Hacer clic en el botón **"Contabilizar en Alegra"**.',
      'El sistema crea los bills en Alegra, adjuntando automáticamente los PDFs de: OC, Recepción y DTE.',
      'Las facturas contabilizadas se marcan con un indicador verde de **"Contabilizada"**.',
    ],
    result: 'Los bills quedan creados en Alegra con todos los documentos adjuntos en formato profesional.',
  },
];

function StepCard({ step, isOpen, onToggle }) {
  return (
    <div className="border border-gray-200 rounded-lg bg-white print:border-gray-300 print:break-inside-avoid">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-50 print:hover:bg-white"
        data-testid={`step-toggle-${step.id}`}
      >
        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#003366] text-white text-sm font-bold shrink-0 print:bg-[#003366]">
          {step.id}
        </span>
        <div className="flex-1">
          <h3 className="font-semibold text-[15px] text-gray-900">{step.title}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{step.location}</p>
        </div>
        <span className="print:hidden">{isOpen ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}</span>
      </button>

      <div className={`${isOpen ? 'block' : 'hidden'} print:!block border-t border-gray-100 p-4 pt-3`}>
        <p className="text-sm text-gray-600 mb-3">{step.desc}</p>
        <ol className="space-y-2 ml-1">
          {step.substeps.map((s, i) => (
            <li key={i} className="flex gap-2 text-sm">
              <span className="text-[#003366] font-semibold shrink-0 w-5 text-right">{i + 1}.</span>
              <span className="text-gray-700">
                {s.split(/\*\*(.*?)\*\*/g).map((part, j) =>
                  j % 2 === 1 ? <strong key={j} className="text-gray-900">{part}</strong> : part
                )}
              </span>
            </li>
          ))}
        </ol>
        <div className="mt-3 flex items-start gap-2 bg-green-50 border border-green-200 rounded p-2.5 print:bg-green-50">
          <ArrowRight className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
          <p className="text-sm text-green-800"><strong>Resultado:</strong> {step.result}</p>
        </div>
      </div>
    </div>
  );
}

export default function Procedimientos() {
  const [openSteps, setOpenSteps] = useState(new Set([1, 2, 3, 4, 5]));

  const toggle = (id) => {
    setOpenSteps(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const expandAll = () => setOpenSteps(new Set([1, 2, 3, 4, 5]));

  return (
    <div className="max-w-4xl mx-auto p-6 print:p-2 print:max-w-none" data-testid="procedimientos-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 print:mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 print:text-xl">Procedimiento: Lotes para Contabilización</h1>
          <p className="text-sm text-gray-500 mt-1">Flujo completo desde la creación del lote hasta el envío a Alegra</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="print:hidden gap-2"
          data-testid="print-btn"
          onClick={() => { expandAll(); setTimeout(() => window.print(), 200); }}
        >
          <Printer className="w-4 h-4" /> Imprimir
        </Button>
      </div>

      {/* Flow diagram */}
      <div className="flex items-center gap-1 mb-6 overflow-x-auto pb-2 print:gap-0.5 print:mb-4">
        {steps.map((s, i) => (
          <div key={s.id} className="flex items-center shrink-0">
            <div className="flex items-center gap-1.5 bg-[#003366] text-white px-3 py-1.5 rounded-full text-xs font-medium print:px-2 print:py-1 print:text-[10px]">
              <span className="font-bold">{s.id}</span>
              <span className="hidden sm:inline">{s.title}</span>
            </div>
            {i < steps.length - 1 && <ArrowRight className="w-4 h-4 text-gray-400 mx-1" />}
          </div>
        ))}
      </div>

      {/* Steps */}
      <div className="space-y-3 print:space-y-2">
        {steps.map(step => (
          <StepCard
            key={step.id}
            step={step}
            isOpen={openSteps.has(step.id)}
            onToggle={() => toggle(step.id)}
          />
        ))}
      </div>

      {/* Footer notes */}
      <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 print:mt-4 print:bg-amber-50">
        <strong>Notas importantes:</strong>
        <ul className="mt-2 space-y-1 ml-4 list-disc">
          <li>Los montos deben incluir IVA (Total con impuesto).</li>
          <li>Si una factura es rechazada en el VB, debe corregirse y volver a incluirse en un nuevo lote.</li>
          <li>Los PDFs adjuntados a Alegra incluyen: Orden de Compra, Comprobante de Recepción y DTE.</li>
          <li>Solo usuarios con rol de Gerencia pueden dar el Visto Bueno.</li>
        </ul>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          nav, aside, header, [data-testid="mobile-menu-toggle"],
          .print\\:hidden { display: none !important; }
          body { font-size: 12px; }
          main { margin: 0 !important; padding: 0 !important; }
          .print\\:break-inside-avoid { break-inside: avoid; }
          .print\\:\\!block { display: block !important; }
        }
      `}</style>
    </div>
  );
}
